import Header from "common/components/Header/Header";
import FilteredList from "common/components/Table/FilteredList";
import useRouter from "common/hooks/use-router";
import useAuth from "common/hooks/useAuth";

import useItem from "common/hooks/useItem";
import useList from "common/hooks/useList";
import React, {
  // eslint-disable-next-line comma-dangle
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";

import { Sector } from "types/Sector";
import AlertsContext from "common/providers/alerts";
import { PiTreePalmLight } from "react-icons/pi";
import usePark from "common/hooks/usePark";
import EnclosureSelect from "components/select/EnclosureSelect/EnclosureSelect";
import SpecySelect from "components/select/SpecySelect/SpecySelect";
import SectorItem from "./SectorItem";

function Sectors() {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { setAlert } = useContext(AlertsContext);

  const { park } = usePark();

  const { user: currentUser } = useAuth();
  const canManageSectors =
    (currentUser?.isSuperadmin ||
      currentUser?.isAdmin ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        currentUser?.administrationPermissions?.sector?.create)) ??
    false;

  const {
    fetchItems,
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    removeListItem,
    itemsCount,
    itemsPerPage,
    error,
  } = useList<Sector>("sectors", {
    defaultFilters:
      currentUser?.isSuperadmin && park?.id
        ? {
            park: park.id,
          }
        : {},
    defaultSort: "name:ASC",
    cachePrefix: "Sectors_all",
  });
  const { removeItem } = useItem<Sector>("sectors", "");

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  const handleDelete = useCallback(
    async (id: any) => {
      try {
        removeListItem(id);
        await removeItem(id);
        fetchItems();
      } catch (e) {
        setAlert(e, "danger");
        return e;
      }
      return true;
    },
    [removeListItem, fetchItems, removeItem, setAlert]
  );

  const handleNavigate = (id: any) => {
    navigate(`/sectors/edit/${id}`);
  };

  const columns = useMemo(
    () => [
      {
        Header: t("forms.name"),
        filterName: "name$containsi",
        filterLabel: t("filters.search"),
        columnClasses: "w-1/2",
      },
      {
        Header: t("forms.enclosures"),
        filterName: "enclosures",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <EnclosureSelect isFilter allowReadOnlyOptions {...props} />
        ),
      },
      {
        Header: t("forms.specy"),
        filterName: "species",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <SpecySelect isFilter allowReadOnlyOptions {...props} />
        ),
      },
    ],
    [t]
  );

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500">
      <Header
        title={t("titlePages.sectors")}
        subtitle={t("titleLegends.sectors")}
        btnTitle={t("newItem.sector")}
        btnSubtitle={t("newItem.sectorLegend")}
        Icon={PiTreePalmLight}
        addLink="/sectors/add"
        addLinkDisabled={!canManageSectors}
        isBeforeFilters
      />

      <FilteredList
        data={items ?? []}
        columns={columns} // used as filter items
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={SectorItem}
        onDelete={handleDelete}
        onNavigate={handleNavigate}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
    </div>
  );
}

export default Sectors;
